import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import _ from 'lodash';
import { ensure } from 'components/sharpr/ensure/ensure';
import { CfgService } from 'components/sharpr/cfg/CfgService.js';
import { AuthTypes } from '../../../sharpr/auth/AuthTypes';
import { IdentityResolves } from '../../../sharpr/identity/IdentityService';

export default function PostRoutes($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.PostDetail', {
		url: '/post/:id/detail',
		controllerAs: 'vm',
		controller: PostDetailCtrl,
		templateUrl: '/ng/hub/Post/Detail/PostDetailCtrl.html?' + window.BUILD,
		resolve: {
			$style: () => AssetManager.css('/ng/hub/Post/Detail/PostDetailCtrl.css'),
			// set window title to post headline
			$title: post => {
				'ngInject';
				return post && post.headline ? _.trunc(post.headline, 50) : 'Error';
			},
			// if post has a classification, load classification icons
			__classification: post => {
				'ngInject';
				if (post.classification) {
					return AssetManager.css('/assets/fonts/Linearicons/style.css');
				} else {
					return true;
				}
			},
			// the post, or if not found, why it wasn't found
			post: (currentUser, $stateParams, $state, Api, Identity, ErrorScreen) => {
				'ngInject';
				const requestParams = $stateParams.isArchived ? { include_deleted: true, archive_action: 'archive' } : {};
				const headers = AuthTypes.getBoardHash() || AuthTypes.getSecretPostHash() ? {} : { Hubs: 'mine' };
				return Api.request('get', '/v3/posts/' + $stateParams.id, requestParams, headers).then(response => {
					if (response.status == 200) {
						return response.data;
					}
					if (response.status == 401) {
						// our auth interceptor will forward the user to the login
						return false;
					}
					const errorDefs = {
						ResourceNotFound: {
							class: 'not-found',
							title: 'That post does not exist',
							message: 'It seems that link is broken.',
							actionText: 'Home',
							actionUrl: '/posts/intelligence',
						},
						ResourceDeleted: {
							class: 'not-found',
							title: 'No longer available',
							message: 'It seems that post was deleted.',
							actionText: 'Home',
							actionUrl: '/posts/intelligence',
						},
						NeedPermission: {
							class: 'not-allowed',
							title: "That post isn't for you",
							message: "For some reason, you don't have access to this post.",
							actionText: 'Home',
							actionUrl: '/posts/intelligence',
						},
						NeedDifferentRole: {
							class: 'not-allowed',
							title: "That post isn't for you",
							message:
								"Your role doesn't have access to this post. Please request access from an editor or your Sharpr account manager.",
							actionText: 'Home',
							actionUrl: '/posts/intelligence',
						},
					};
					let errorJson = response.headers('API-Response-Errors');
					let errors;
					try {
						errors = JSON.parse(errorJson).filter(err => {
							return typeof err.type !== 'undefined' && err.type in errorDefs;
						});
						// eslint-disable-next-line no-empty
					} catch (e) {}
					let type = errors && errors[0] ? errors[0].type : 'ResourceNotFound';
					ErrorScreen.show(errorDefs[type]);
					return false;
				});
			},
			// list of attachments
			attachments: ApiResourcify.endpoint('/v3/posts/:id/attachments'),
			// previews of first 3 attachments
			previews: ApiResourcify.endpoint('/v3/posts/:id/attachments/previews?limit=3&page=1'),
			// current user (from email or from logged in)
			currentUser: (Api, Identity) => {
				'ngInject';
				if (AuthTypes.getBoardHash() || AuthTypes.getSecretPostHash()) {
					return {};
				}
				const endpoint = AuthTypes.getEmailHash() ? '/users/bar' : '/v3/users/me';
				return Api.get(endpoint).then(user => {
					Identity.setUser(user);
					return user;
				});
			},
			// config settings for this posts' hub and this user
			__preferences: (Api, $stateParams) => {
				'ngInject';
				return Api.get(`/posts/${$stateParams.id}/preferences`).then(
					prefs => {
						CfgService.apply(prefs);
					},
					error => {
						console.error('Error loading preferences: ', error);
					}
				);
			},
			...(() => {
				const resolves = {};
				IdentityResolves(resolves);
				return resolves;
			})(),
		},
	});
}

class PostDetailCtrl {
	constructor($timeout, $window, Api, Cfg, post, Post, attachments, previews, currentUser, Theme, EventTracker) {
		'ngInject';
		if (!post) {
			// post resolve should show error screen
			return;
		}
		this.isBar = Boolean(AuthTypes.getEmailHash());
		// useful functions/libraries
		this.$timeout = $timeout;
		this.$window = $window;
		this.Api = Api;
		this.Cfg = Cfg;
		this.EventTracker = EventTracker;
		// set geometry based on window size
		this.setGeometry();
		window.addEventListener(
			'resize',
			_.throttle(
				() => {
					this.setGeometry();
					$timeout(() => {});
				},
				150,
				{ leading: false, trailing: true }
			)
		);
		// state
		post.attachments = ensure.array(attachments);
		post.attachment_previews = ensure.array(previews);
		this.currentUser = ensure.object(currentUser);
		this.usersToNotify = [];
		//console.log('post bar', likes, post, this.currentUser);

		// also state: the post
		if (post.published_by) {
			post.published_by.id = post.published_by.id.toString();
		}
		if (!post.source_name && /^https?:\/\/([^/]+).*/.test(post.source_link)) {
			post.source_name = post.source_link.replace(/^https?:\/\/([^/]+).*/, '$1');
		}
		this.post = post;
		this.PostService = Post;
		Theme.apply(post.colors).then(() => {
			$timeout(() => {
				this.themeLoaded = true;
			});
		});
		this.logo = `/assets/theme/${post.colors.code}/on-light.png`;

		// track that we viewed this post
		this.trackView();
		this.setupMessenger();
		this.isSecretPostLink = !!AuthTypes.getSecretPostHash();
		this.setupLogo();

		this.setupPreferences();
	}

	setupPreferences() {
		this.hideLikesAndComments =
			this.Cfg.readAsBool('Features.hide_likes_and_comments') || AuthTypes.getBoardHash() || AuthTypes.getSecretPostHash();
		this.privateComments = this.Cfg.readAsBool('Features.private_comments');
		this.displayKnowledgebaseLink = this.Cfg.readAsBool('KnowledgebaseLink.Display');
		this.knowledgebaseLinkUrl = this.Cfg.read('KnowledgebaseLink.PostDetails.Page');
		this.showInsightsBubble = this.Cfg.readAsBool('UI.post_show_insights_bubble');
	}

	trackView() {
		setTimeout(() => {
			let origin = 'Direct link';
			if (this.isBar) {
				origin = 'Email newsletter';
			}
			if (window.opener) {
				origin = 'Internal link';
			}
			this.EventTracker.save('post_view', {
				post_id: this.post.id,
				origin,
			});
		}, 750);
	}
	setupMessenger() {
		try {
			this.messenger = new window.CrossWindowMessenger(window.parent, '*');
		} catch (e) {
			//do noting since this functionality isn't mission critical. It's only used when react is iframing in angular
		}
	}

	setGeometry() {
		this.pinWidth = Math.min(document.documentElement.clientWidth, 800);
		this.geometry = `pin-${this.pinWidth}`;
	}

	postMessage(action, values) {
		if (this.messenger) {
			this.messenger.post(action, values);
		}
	}

	switchLikeMode() {
		let headers = { Hubs: 'mine' };
		// if switching to the user not liking it, we need to remove their like from the list
		if (this.post.user_like_id) {
			// need to unlike
			let delUrl = this.isBar ? '/bar/posts/likes' : `/posts/${this.post.id}/likes/${this.post.user_like_id}`;
			this.Api.delete(delUrl, null, headers);
			this.postMessage('unfavorite', { postId: this.post.id });
			this.post.user_like_id = null;
		} else {
			// need to add a like
			let addUrl = this.isBar ? '/bar/posts/likes' : `/posts/${this.post.id}/likes`;
			this.Api.create(
				addUrl,
				{
					user: this.isBar ? undefined : this.currentUser.id,
				},
				headers
			).then(newId => {
				this.post.user_like_id = newId;
			});
			// temporarily change it so that UI updates
			this.post.user_like_id = -1;
		}
	}

	scrollToComment() {
		this.showCommentBox = true;

		// without a timeout, #AddComment is still hidden, so it doesn't
		/// scroll at all
		this.$timeout(function () {
			document.querySelector('sh-post-history .form-input').scrollIntoView();
		}, 0);
	}

	searchByTag(tag) {
		if (this.isBar && this.currentUser.role != 'intel leader') {
			// if we are an observer from an email and don't have access to the intel tab
			return;
		}
		window.location.href = `#/posts?layout=pin&finetune=(tags:!('${encodeURIComponent(tag.name)}'))`;
	}

	getTranscript() {
		if (this.transcript) {
			return this.transcript;
		}
		this.transcript = '';
		// Pull the first non-empty full_text for a video attachment.
		ensure.array(this.post.attachments).some(attachment => {
			if (['video', 'audio'].indexOf(attachment.icon) > -1 && attachment.full_text && attachment.full_text.length) {
				this.transcript = attachment.full_text;
				this.transcriptTitle = attachment.filename;
				return true;
			}
		});
		return this.transcript;
	}

	viewOriginalText() {
		this.PostService.translationViewOriginal(this.post.id, this.post.translated_from).then(
			() => {},
			() => {}
		);
	}

	setupLogo() {
		const isModal = document.querySelector('.sh-modal-header') !== null;
		if (isModal) {
			// Post details page is rendered in modal from insights page
			this.logoHref = false;
		} else {
			// private/public link
			this.logoHref =
				`${this.$window.BASE_URL}/users/setclient/${this.post.hub.id}?backTo=` +
				encodeURIComponent(`${this.$window.BASE_URL}/core/insights?term=post_id:${this.post.id}`);
		}
	}

	clickToSource() {
		this.EventTracker.save('click_source', {
			post_id: this.post.id,
			url: this.post.source_link,
		});
	}
}
